import {
  ADD_OFFER_LIST_EXTRA,
  API_CALL_FAILURE,
  API_CALL_REQUEST,
  API_CALL_SUCCESS,
  BEDBANK_UPDATE_PRICING_DATA,
  CLEAR_OFFERS_WITH_DISCOUNT,
  END_OFFER_LIST_STREAM,
  INITIALISE_BNBL_MODAL,
  OFFERS_CLEAR,
  OFFER_PUSH_TO_LIST_BOTTOM,
  OFFER_SET_SEARCH_RESULT_METADATA,
  OFFER_SET_TOUR_SEARCH_RESULT_METADATA,
  OFFER_UPDATE_AVAILABLE_RATES,
  OFFER_UPDATE_PRICING_DATA,
  PROPERTY_UPDATE_PRICING_DATA,
  REMOVE_OFFER,
  SET_OFFER_LIST_BOUNDS,
  SET_OFFER_LIST_DESCRIPTION_LOADING,
  SET_OFFER_LIST_SELECTED_FILTERS,
  SET_OFFER_LIST_ERROR,
  SET_OFFER_LIST_SOCKET_STATUS,
  SET_OFFER_LIST_VALID_OFFER,
  SET_TOUR_V2_SELECTED_PACKAGE_TYPE,
  UPDATE_BNBL_OCCUPANCIES,
  UPDATE_BNBL_PACKAGE_VIEW,
  UPDATE_GIFTING_BNBL_MODAL,
  UPDATE_OFFER_METADATA_DESCRIPTION,
} from 'actions/actionConstants'
import {
  FETCH_ALTERNATIVE_OFFERS,
  FETCH_AVAILABILITY_RATES_FOR_OFFER,
  FETCH_BEDBANK_OFFER,
  FETCH_BEDBANK_OFFERS_RATES,
  FETCH_BEDBANK_OFFER_CALENDAR,
  FETCH_BEDBANK_OFFER_RATES,
  FETCH_BEDBANK_OFFER_RATES_BULK,
  FETCH_BEDBANK_OFFER_SUMMARIES,
  FETCH_BEDBANK_OFFER_WITH_RATES,
  FETCH_BEST_OFFER_FOR_PROPERTY,
  FETCH_BEST_PRICE_FOR_OFFER,
  FETCH_OFFER,
  FETCH_OFFERS,
  FETCH_OFFER_FLIGHT_PRICE,
  FETCH_OFFER_LIST,
  FETCH_OFFER_LIST_FILTERS,
  FETCH_OFFER_LOCATION_BREADCRUMBS,
  FETCH_OFFER_META_DATA,
  FETCH_OFFER_PROPERTY_SURROUNDINGS,
  FETCH_OFFER_SUMMARIES,
  FETCH_PACKAGE_RECOMMENDATIONS,
  FETCH_PROPERTY,
  FETCH_PROPERTY_ANYTIME_RATE,
  FETCH_PROPERTY_OFFER_MAPPING,
  FETCH_PROPERTY_RATES,
  FETCH_PROPERTY_TAGS,
  FETCH_PROPERTY_NEARBY,
  FETCH_OFFER_TAGS,
  FETCH_RELATED_TRAVEL_ITEMS,
  FETCH_SURCHARGE_MARGIN,
  FETCH_TOURV2_OFFER,
  FETCH_TRADER_INFORMATION,
  FETCH_METASEARCH_OFFER,
} from 'actions/apiActionConstants'
import { DEFAULT_OCCUPANCY_FOR_BNBL } from 'components/OfferPage/BnblModal/utils'
import {
  EmptyArray,
  arrayToObject,
  sum,
  unique,
} from 'lib/array/arrayUtils'
import { floatify } from 'lib/maths/mathUtils'
import { EmptyObject, mergeObjects, omitKeys } from 'lib/object/objectUtils'
import { apiReducerSwitch, createAppReducer } from 'lib/redux/reducerUtils'
import type { AnyAction } from 'redux'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { getOfferId } from 'lib/offer/offerUtils'

export const initialOfferState: App.OfferState = {
  offers: {},
  tourV2Offers: {},
  tourV2SelectedPackageType: {},
  bedbankOfferRates: {},
  bedbankOfferRatesLoading: {},
  bedbankOfferCalendar: {},
  bedbankOfferSummaries: {},
  bedbankOffers: {},
  bnblModal: {},
  offerRatesLoading: {},
  bedbankOfferCalendarLoading: {},
  offersLoading: {},
  offersBypassRegion: {},
  offerErrors: {},
  offerSummaries: {},
  offerSummariesLoading: {},
  offerSummaryErrors: {},
  offerLists: {},
  offerListAdjustedOffers: {},
  offerListFilterOptions: {},
  offerBestPrices: {},
  offerPricesLoading: {},
  offerPricesErrors: {},
  offerAvailableRates: {},
  offerAvailableRatesByOccupancy: {},
  relatedTravelItems: {},
  alternativeOffersForProperty: {},
  searchResultMetadata: {
    distanceFromSearchTarget: {},
    offerAvailabilityFromSearchTarget: {},
    offerSuggestedDates: {},
    offerMetaData: {},
    pendingOfferDescriptions: {},
  },
  tourSearchResultMetadata: {
    tourMetadata: {},
  },
  traderInformation: {},
  bestPropertyOffer: {},
  locationBreadcrumbs: {},
  offersRatesBulkLoading: false,
  alternativeDates: {},
  alternativeDatesLoading: {},
  surchargeMargins: {},
  properties: {},
  propertyAnytimeRates: {},
  propertyOfferMappings: {},
  propertyRates: {},
  propertyTags: {},
  propertyNearby: {},
  offerPropertySurroundings: {},
  offerTags: {},
  offersWithDiscount: {},
  packageRecommendations: {},
}

function getEmptyOfferListState(key: string): App.OfferList {
  return {
    fetching: false,
    offerIds: [],
    mapOfferIds: [],
    key,
  }
}

function mergeOfferListMetaDataWithDescriptions(
  existingList: Record<string, App.OfferListMetaData | undefined> | undefined,
  incomingList: Record<string, App.OfferListMetaData | undefined> | undefined,
  pendingDescriptions?: Record<string, Array<string> | undefined>,
): Record<string, App.OfferListMetaData | undefined> {
  const merged: Record<string, App.OfferListMetaData | undefined> = { ...(existingList || {}) }

  if (!incomingList) return merged

  for (const [offerId, incomingMeta] of Object.entries(incomingList)) {
    if (!incomingMeta) continue
    const existingMeta = merged[offerId]
    const pendingDescription = pendingDescriptions?.[offerId]
    merged[offerId] = {
      ...existingMeta,
      ...incomingMeta,
      description: incomingMeta.description ?? existingMeta?.description ?? pendingDescription,
    }
  }

  return merged
}

function fetchOfferRequest(state: App.OfferState, action: AnyAction): Partial<App.OfferState> {
  const nextState: Partial<App.OfferState> = {
    offersLoading: {
      ...state.offersLoading,
      [action.offerId]: true,
    },
  }

  if (state.offerErrors[action.offerId]) {
    // reset the error state as we're fetching it again
    nextState.offerErrors = {
      ...state.offerErrors,
      [action.offerId]: undefined,
    }
  }

  return nextState
}

function fetchOffersRequest(state: App.OfferState, action: AnyAction & { offerIds: Array<string> }): Partial<App.OfferState> {
  const nextState: Partial<App.OfferState> = {
    offersLoading: {
      ...state.offersLoading,
      ...arrayToObject(action.offerIds, (id: string) => id, () => true),
    },
  }

  if (action.offerIds.some(id => !!state.offerErrors[id])) {
    // only change the offerErrors if a value actually needs to be reset
    nextState.offerErrors = {
      ...state.offerErrors,
      ...arrayToObject(action.offerIds, (id: string) => id, () => undefined),
    }
  }

  return nextState
}

function fetchOfferError(state: App.OfferState, action: AnyAction): Partial<App.OfferState> {
  return {
    offerErrors: {
      ...state.offerErrors,
      [action.offerId]: action.error,
    },
    offersLoading: {
      ...state.offersLoading,
      [action.offerId]: false,
    },
  }
}

const apiCalls = apiReducerSwitch<App.OfferState>({
  [FETCH_PROPERTY]: {
    request: (state, action) => ({
      properties: {
        ...state.properties,
        [action.propertyId]: {
          fetching: true,
          error: undefined,
        },
      },
    }),
    success: (state, action) => ({
      properties: {
        ...state.properties,
        [action.propertyId]: {
          fetching: false,
          property: action.data,
        },
      },
    }),
    failure: (state, action) => ({
      properties: {
        ...state.properties,
        [action.propertyId]: {
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
  [FETCH_PROPERTY_ANYTIME_RATE]: {
    request: (state, action) => ({
      propertyAnytimeRates: {
        ...state.propertyAnytimeRates,
        [action.propertyId]: {
          fetching: true,
          error: undefined,
        },
      },
    }),
    success: (state, action) => ({
      propertyAnytimeRates: {
        ...state.propertyAnytimeRates,
        [action.propertyId]: {
          fetching: false,
          anytimeRate: action.data?.[0],
          anytimePromotionRate: action.data?.[1],
        },
      },
    }),
    failure: (state, action) => ({
      propertyAnytimeRates: {
        ...state.propertyAnytimeRates,
        [action.propertyId]: {
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
  [FETCH_PROPERTY_OFFER_MAPPING]: {
    request: (state, action) => ({
      propertyOfferMappings: {
        ...state.propertyOfferMappings,
        [action.offerId]: {
          fetching: true,
          error: undefined,
        },
      },
    }),
    success: (state, action) => ({
      propertyOfferMappings: {
        ...state.propertyOfferMappings,
        [action.offerId]: {
          fetching: false,
          mapping: action.data,
        },
      },
    }),
    failure: (state, action) => ({
      propertyOfferMappings: {
        ...state.propertyOfferMappings,
        [action.offerId]: {
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
  [FETCH_PROPERTY_RATES]: {
    request: (state, action) => {
      const updates: App.OfferState['propertyRates'] = {}
      for (const key of Object.values(action.keys)) {
        updates[key] = {
          ...state.propertyRates[key], // Keep old data around while we're fetching.
          fetching: true,
          error: undefined,
        }
      }
      return { propertyRates: { ...state.propertyRates, ...updates } }
    },
    success: (state, action) => {
      const updates: App.OfferState['propertyRates'] = {}
      for (const key of Object.values(action.keys)) {
        updates[key] = { fetching: false, rooms: action.data[key] ?? EmptyArray }
      }
      return { propertyRates: { ...state.propertyRates, ...updates } }
    },
    failure: (state, action) => {
      const updates: App.OfferState['propertyRates'] = {}
      for (const key of Object.values(action.keys)) {
        updates[key] = { fetching: false, error: action.error }
      }
      return { propertyRates: { ...state.propertyRates, ...updates } }
    },
  },
  [FETCH_OFFER_LOCATION_BREADCRUMBS]: {
    request: (state, action) => ({
      locationBreadcrumbs: {
        ...state.locationBreadcrumbs,
        [action.propertyId]: {
          fetching: true,
        },
      },
    }),
    success: (state, action) => ({
      locationBreadcrumbs: {
        ...state.locationBreadcrumbs,
        [action.propertyId]: {
          fetching: false,
          locations: action.data,
        },
      },
    }),
    failure: (state, action) => ({
      locationBreadcrumbs: {
        ...state.locationBreadcrumbs,
        [action.propertyId]: {
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
  [FETCH_BEST_OFFER_FOR_PROPERTY]: {
    request: (state, action) => ({
      bestPropertyOffer: {
        ...state.bestPropertyOffer,
        [action.propertyId]: {
          fetching: true,
        },
      },
    }),
    success: (state, action) => ({
      bestPropertyOffer: {
        ...state.bestPropertyOffer,
        [action.propertyId]: {
          fetching: false,
          offerId: action.data.offerId,
          type: action.data.type,
        },
      },
    }),
    failure: (state, action) => ({
      bestPropertyOffer: {
        ...state.bestPropertyOffer,
        [action.propertyId]: {
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
  [FETCH_OFFER_LIST_FILTERS]: {
    request: (state, action) => ({
      offerListFilterOptions: {
        ...state.offerListFilterOptions,
        [action.key]: {
          ...state.offerListFilterOptions[action.key],
          error: undefined,
          fetching: true,
        },
      },
    }),
    success: (state, action) => {
      const offerLists = state.offerLists
      const prevState = offerLists[action.key] ?? getEmptyOfferListState(action.key)

      // For streaming we add the offerCount of the list on FETCH_OFFER_LIST_FILTERS
      const nextOfferCount = action.data.offerCount === undefined ? prevState?.offerCount : action.data.offerCount

      return {
        offerLists: {
          ...offerLists,
          [action.key]: {
            ...prevState,
            offerCount: nextOfferCount,
          },
        },
        offerListFilterOptions: {
          ...state.offerListFilterOptions,
          [action.key]: {
            ...state.offerListFilterOptions[action.key]!,
            filters: action.data.filters,
            filterOrder: action.data.filterOrder,
            orderedFilters: action.data.orderedFilters,
            fetching: false,
          },
        },
      }
    },
    failure: (state, action) => ({
      offerListFilterOptions: {
        ...state.offerListFilterOptions,
        [action.key]: {
          ...state.offerListFilterOptions[action.key],
          error: action.error,
          fetching: false,
        },
      },
    }),
  },
  [FETCH_OFFER]: {
    request: fetchOfferRequest,
    success: (state, action) => ({
      offers: {
        ...state.offers,
        [action.offerId]: action.data as App.Offer,
      },
      offersLoading: {
        ...state.offersLoading,
        [action.offerId]: false,
      },
      ...(action.bypassRegionFilter ? {
        offersBypassRegion: {
          ...state.offersBypassRegion,
          [action.offerId]: true,
        },
      } : {}),
    }),
    failure: fetchOfferError,
  },
  [FETCH_OFFER_META_DATA]: {
    request: (state) => ({
      searchResultMetadata: {
        ...state.searchResultMetadata,
      },
    }),
    success: (state, action) => {
      const { bedbankRatesData } = action.data

      const bedbankRatesUpdate = bedbankRatesData
        ? {
            bedbankOfferRates: {
              ...state.bedbankOfferRates,
              [action.offerId]: {
                ...state.bedbankOfferRates[action.offerId],
                [bedbankRatesData.filterKey]: bedbankRatesData.rates,
              },
            },
            bedbankOfferRatesLoading: {
              ...state.bedbankOfferRatesLoading,
              [action.offerId]: {
                ...state.bedbankOfferRatesLoading[action.offerId],
                [bedbankRatesData.filterKey]: false,
              },
            },
            offerRatesLoading: {
              ...state.offerRatesLoading,
              [action.offerId]: false,
            },
          }
        : {}

      return {
        ...bedbankRatesUpdate,
        searchResultMetadata: {
          ...state.searchResultMetadata,
          offerMetaData: {
            ...state.searchResultMetadata.offerMetaData,
            [action.alternativeOfferMetaDataKey]: {
              ...(state.searchResultMetadata.offerMetaData[action.alternativeOfferMetaDataKey] || {}),
              [action.offerId]: action.data.alternativeOfferMetaData,
            },
            [action.originalOfferMetaDataKey]: {
              ...(state.searchResultMetadata.offerMetaData[action.originalOfferMetaDataKey] || {}),
              [action.offerId]: action.data.originalOfferMetaData,
            },
          },
        },
      }
    },
    failure: (state) => ({
      searchResultMetadata: {
        ...state.searchResultMetadata,
      },
    }),
  },
  // Handles the combined offer+rates payload and updates both state branches together.
  [FETCH_BEDBANK_OFFER_WITH_RATES]: {
    request: fetchOfferRequest,
    success: (state, action) => {
      const { offer, offerMetaData, bedbankRatesData } = action.data

      const bedbankRatesUpdate = bedbankRatesData
        ? {
            bedbankOfferRates: {
              ...state.bedbankOfferRates,
              [action.offerId]: {
                ...state.bedbankOfferRates[action.offerId],
                [bedbankRatesData.filterKey]: bedbankRatesData.rates,
              },
            },
            bedbankOfferRatesLoading: {
              ...state.bedbankOfferRatesLoading,
              [action.offerId]: {
                ...state.bedbankOfferRatesLoading[action.offerId],
                [bedbankRatesData.filterKey]: false,
              },
            },
            offerRatesLoading: {
              ...state.offerRatesLoading,
              [action.offerId]: false,
            },
          }
        : {}

      return {
        ...bedbankRatesUpdate,
        bedbankOffers: {
          ...state.bedbankOffers,
          [action.offerId]: offer,
        },
        offersLoading: {
          ...state.offersLoading,
          [action.offerId]: false,
        },
        searchResultMetadata: {
          ...state.searchResultMetadata,
          offerMetaData: {
            ...state.searchResultMetadata.offerMetaData,
            [action.offerMetaDataKey]: {
              ...(state.searchResultMetadata.offerMetaData[action.offerMetaDataKey] || {}),
              [action.offerId]: offerMetaData,
            },
          },
        },
      }
    },
    failure: fetchOfferError,
  },
  [FETCH_TRADER_INFORMATION]: {
    request: (state, action) => ({
      traderInformation: {
        ...state.traderInformation,
        [action.offerId]: {
          data: null,
          state: 'loading',
        },
      },
    }),
    success: (state, action) => ({
      traderInformation: {
        ...state.traderInformation,
        [action.offerId]: {
          data: action.data,
          state: 'done',
        },
      },
    }),
    failure: (state, action) => ({
      traderInformation: {
        ...state.traderInformation,
        [action.offerId]: {
          data: null,
          state: 'error',
        },
      },
    }),
  },
  [FETCH_OFFERS]: {
    request: fetchOffersRequest,
    success: (state, action) => {
      const foundOfferIds = new Set(action.data?.map(o => o.id))
      // missing offer ids are treated as errors
      const missingOfferIds = action.offerIds.filter(id => !foundOfferIds.has(id))

      return {
        offers: {
          ...state.offers,
          ...arrayToObject(action.data, getOfferId),
        },
        offerSummaries: omitKeys(
          action.offerIds,
          state.offerSummaries,
        ),
        offerErrors: missingOfferIds.length > 0 ? {
          ...state.offerErrors,
          ...arrayToObject(
            missingOfferIds,
            (id: string) => id,
            () => true,
          ),
        } : state.offerErrors,
        offersLoading: {
          ...state.offersLoading,
          ...arrayToObject(
            action.offerIds,
            (id: string) => id,
            () => false,
          ),
        },
      }
    },
    failure: (state, action) => ({
      offerErrors: {
        ...state.offerErrors,
        ...arrayToObject(action.offerIds, (id: string) => id, () => action.error),
      },
      offersLoading: {
        ...state.offersLoading,
        ...arrayToObject(action.offerIds, (id: string) => id, () => false),
      },
    }),
  },
  [FETCH_OFFER_SUMMARIES]: {
    request: (state, action) => ({
      offerSummariesLoading: {
        ...state.offerSummariesLoading,
        ...arrayToObject(action.offerIds, (id: string) => id, () => true),
      },
      offerSummaryErrors: {
        ...state.offerSummaryErrors,
        ...arrayToObject(action.offerIds, (id: string) => id, () => undefined),
      },
    }),
    success: (state, action) => ({
      offerSummaries: {
        ...state.offerSummaries,
        ...arrayToObject(action.data, getOfferId),
      },
      offerSummariesLoading: {
        ...state.offerSummariesLoading,
        ...arrayToObject(
          action.offerIds,
          (id: string) => id,
          () => false,
        ),
      },
    }),
    failure: (state, action) => ({
      offerSummaryErrors: {
        ...state.offerSummaryErrors,
        ...arrayToObject(action.offerIds, (id: string) => id, () => action.error),
      },
      offerSummariesLoading: {
        ...state.offerSummariesLoading,
        ...arrayToObject(action.offerIds, (id: string) => id, () => false),
      },
    }),
  },
  [FETCH_OFFER_LIST]: {
    request: (state, action) => {
      const prevState = state.offerLists[action.key]

      return {
        offerLists: {
          ...state.offerLists,
          [action.key]: {
            ...prevState,
            offerIds: EmptyArray,
            mapOfferIds: EmptyArray,
            key: action.key,
            error: undefined,
            fetching: true,
            searchId: action.searchId,
            socketListenersStatus: 'listening',
            descriptionLoading: undefined,
          },
        },
        searchResultMetadata: {
          ...state.searchResultMetadata,
          pendingOfferDescriptions: {
            ...state.searchResultMetadata.pendingOfferDescriptions,
            [action.key]: undefined,
          },
        },
      }
    },
    success: (state, action) => {
      // from search response (non-streaming)
      const searchId = action.searchId
      const offerLists = state.offerLists
      const prevState = offerLists[action.key]

      // If we have received streamed offers we append them to the offerIds array
      // Use unique() to prevent duplicates that can occur from socket reconnections or duplicate server messages
      const streamedOfferIds: Array<string> | undefined = action.data.streamedOfferIds ??
        (action.data.streamedOfferId ? [action.data.streamedOfferId] : undefined)
      const streamedMapOfferIds: Array<string> | undefined = action.data.streamedMapOfferIds

      let nextOfferIds = prevState?.offerIds ?? []
      if (streamedOfferIds) {
        nextOfferIds = unique([...(prevState?.offerIds || []), ...streamedOfferIds])
      } else if (action.data.mainOfferList !== undefined) {
        nextOfferIds = action.data.mainOfferList
      }

      let nextMapOfferIds = prevState?.mapOfferIds ?? EmptyArray
      if (streamedMapOfferIds) {
        nextMapOfferIds = unique([...(prevState?.mapOfferIds || []), ...streamedMapOfferIds])
      } else if (!streamedOfferIds && action.data.mainOfferList !== undefined) {
        // Non-streaming full list replace — clear map-dedicated pins
        nextMapOfferIds = EmptyArray
      }

      // We do not update the offerCount if we have streamed offers
      const nextOfferCount = action.data.offerCount === undefined ? prevState?.offerCount : action.data.offerCount

      // Map-only flushes must not clear list fetching (avoids premature extend-to).
      const isListStreamUpdate = !!streamedOfferIds || action.data.mainOfferList !== undefined
      const nextFetching = isListStreamUpdate ? false : (prevState?.fetching ?? false)

      return {
        offerLists: {
          ...offerLists,
          [action.key]: {
            key: action.key,
            ...prevState,
            ...(searchId && { searchId }),
            offerIds: nextOfferIds,
            mapOfferIds: nextMapOfferIds,
            fetching: nextFetching,
            offerCount: nextOfferCount,
            searchVertical: action.data.searchVertical,
            metaSearchOfferTypes: action.data.metaSearchOfferTypes,
            metaSearchOfferDates: action.data.metaSearchOfferDates,
            error: undefined,
          },
        },
      }
    },
    failure: (state, action) => {
      const prevState = state.offerLists[action.key]

      const newState = {
        offerLists: {
          ...state.offerLists,
          [action.key]: {
            ...prevState!,
            offerIds: EmptyArray,
            mapOfferIds: EmptyArray,
            fetching: false,
            error: action.error,
          },
        },
      }

      return newState
    },
  },
  [FETCH_PROPERTY_TAGS]: {
    request: (state, action) => ({
      propertyTags: {
        ...state.propertyTags,
        [action.propertyId]: {
          tags: [],
          fetching: true,
        },
      },
    }),
    success: (state, action) => ({
      propertyTags: {
        ...state.propertyTags,
        [action.propertyId]: {
          tags: action.data.tags,
          fetching: false,
        },
      },
    }),
    failure: (state, action) => ({
      propertyTags: {
        ...state.propertyTags,
        [action.propertyId]: {
          tags: [],
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
  [FETCH_PROPERTY_NEARBY]: {
    request: (state, action) => ({
      propertyNearby: {
        ...state.propertyNearby,
        [action.propertyId]: {
          nearby: {},
          fetching: true,
        },
      },
    }),
    success: (state, action) => ({
      propertyNearby: {
        ...state.propertyNearby,
        [action.propertyId]: {
          nearby: action.data,
          fetching: false,
        },
      },
    }),
    failure: (state, action) => ({
      propertyNearby: {
        ...state.propertyNearby,
        [action.propertyId]: {
          nearby: {},
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
  [FETCH_OFFER_PROPERTY_SURROUNDINGS]: {
    request: (state, action) => ({
      offerPropertySurroundings: {
        ...state.offerPropertySurroundings,
        [action.cacheKey]: {
          fetching: true,
          error: undefined,
        },
      },
    }),
    success: (state, action) => ({
      offerPropertySurroundings: {
        ...state.offerPropertySurroundings,
        [action.cacheKey]: {
          surroundings: action.data,
          fetching: false,
          error: undefined,
        },
      },
    }),
    failure: (state, action) => ({
      offerPropertySurroundings: {
        ...state.offerPropertySurroundings,
        [action.cacheKey]: {
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
  [FETCH_OFFER_TAGS]: {
    request: (state, action) => ({
      offerTags: {
        ...state.offerTags,
        [action.cacheKey]: {
          tags: [],
          fetching: true,
        },
      },
    }),
    success: (state, action) => ({
      offerTags: {
        ...state.offerTags,
        [action.cacheKey]: {
          tags: action.data,
          fetching: false,
        },
      },
    }),
    failure: (state, action) => ({
      offerTags: {
        ...state.offerTags,
        [action.cacheKey]: {
          tags: [],
          fetching: false,
        },
      },
    }),
  },
  [FETCH_BEST_PRICE_FOR_OFFER]: {
    request: (state, action) => ({
      offerPricesLoading: {
        ...state.offerPricesLoading,
        [action.offerId]: {
          ...state.offerPricesLoading[action.offerId],
          [action.key]: true,
        },
      },
    }),
    success: (state, action) => ({
      offerPricesLoading: {
        ...state.offerPricesLoading,
        [action.offerId]: {
          ...state.offerPricesLoading[action.offerId],
          [action.key]: false,
        },
      },
      offerBestPrices: {
        ...state.offerBestPrices,
        [action.offerId]: {
          ...state.offerBestPrices[action.offerId],
          [action.key]: action.data,
        },
      },
      offerPricesErrors: {
        ...state.offerPricesErrors,
        [action.offerId]: {
          ...state.offerPricesErrors[action.offerId],
          [action.key]: null,
        },
      },
    }),
    failure: (state, action) => ({
      offerPricesLoading: {
        ...state.offerPricesLoading,
        [action.offerId]: {
          ...state.offerPricesLoading[action.offerId],
          [action.key]: false,
        },
      },
      offerPricesErrors: {
        ...state.offerPricesErrors,
        [action.offerId]: {
          ...state.offerPricesErrors[action.offerId],
          [action.key]: action.error,
        },
      },
    }),
  },
  [FETCH_AVAILABILITY_RATES_FOR_OFFER]: {
    request: (state, action) => {
      const isFullLoad = action.packageIds === undefined
      /**
       * If `action.packageIds` is set, this means we're doing a partial load. Partial loads can
       * accumulate, and we assume that `action.packageIds` will not include IDs that have already
       * been loaded. However, if `action.packageIds` is unset, then we're doing a full load and
       * discarding any accumulated partial loads.
       */
      const currentOfferAvailableRates = state.offerAvailableRates[action.offerId]?.[action.key] || { rates: [] }
      // We assume that `action.packageIds` contains no duplicates.
      const newOfferAvailableRatesPackageIds = isFullLoad
        ? undefined : [...(currentOfferAvailableRates?.packageIds ?? []), ...action.packageIds!]
      const currentOfferAvailableRatesByOccupancy = state.offerAvailableRatesByOccupancy[action.offerId]?.[action.key] || { rates: [] }
      const newOfferAvailableRatesByOccupancyPackageIds = isFullLoad
        ? undefined : [...(currentOfferAvailableRatesByOccupancy?.packageIds ?? []), ...action.packageIds!]
      return {
        offerAvailableRates: {
          ...state.offerAvailableRates,
          [action.offerId]: {
            ...state.offerAvailableRates[action.offerId],
            [action.key]: {
              ...currentOfferAvailableRates,
              key: action.key,
              packageIds: newOfferAvailableRatesPackageIds,
              error: undefined,
              fetching: true,
            },
          },
        },
        offerAvailableRatesByOccupancy: {
          ...state.offerAvailableRatesByOccupancy,
          [action.offerId]: {
            ...state.offerAvailableRatesByOccupancy[action.offerId],
            [action.key]: {
              ...currentOfferAvailableRatesByOccupancy,
              key: action.key,
              packageIds: newOfferAvailableRatesByOccupancyPackageIds,
              error: undefined,
              fetching: true,
            },
          },
        },
      }
    },
    success: (state, action) => {
      const isFullLoad = action.packageIds === undefined
      const currentOfferAvailableRates = state.offerAvailableRates[action.offerId]?.[action.key]?.rates || []
      const newOfferAvailableRates = isFullLoad ? action.data : [...currentOfferAvailableRates, ...action.data]
      const currentOfferAvailableRatesByOccupancy = state.offerAvailableRatesByOccupancy[action.offerId]?.[action.key]?.rates || []
      const newOfferAvailableRatesByOccupancy = isFullLoad ? action.data : [...currentOfferAvailableRatesByOccupancy, ...action.data]
      return {
        offerAvailableRates: {
          ...state.offerAvailableRates,
          [action.offerId]: {
            ...state.offerAvailableRates[action.offerId],
            [action.key]: {
              ...state.offerAvailableRates[action.offerId]?.[action.key],
              key: action.key,
              rates: newOfferAvailableRates,
              error: undefined,
              fetching: false,
            },
          },
        },
        offerAvailableRatesByOccupancy: {
          ...state.offerAvailableRatesByOccupancy,
          [action.offerId]: {
            ...state.offerAvailableRatesByOccupancy[action.offerId],
            [action.key]: {
              ...state.offerAvailableRatesByOccupancy[action.offerId]?.[action.key],
              key: action.key,
              rates: newOfferAvailableRatesByOccupancy,
              error: undefined,
              fetching: false,
            },
          },
        },
      }
    },
    failure: (state, action) => ({
      offerAvailableRates: {
        ...state.offerAvailableRates,
        [action.offerId]: {
          ...state.offerAvailableRates[action.offerId],
          [action.key]: {
            key: action.key,
            rates: [],
            error: action.error,
            fetching: false,
          },
        },
      },
      offerAvailableRatesByOccupancy: {
        ...state.offerAvailableRatesByOccupancy,
        [action.offerId]: {
          ...state.offerAvailableRatesByOccupancy[action.offerId],
          [action.key]: {
            key: action.key,
            rates: [],
            error: action.error,
            fetching: false,
          },
        },
      },
    }),
  },
  [FETCH_BEDBANK_OFFER]: {
    request: fetchOfferRequest,
    success: (state, action) => ({
      bedbankOffers: {
        ...state.bedbankOffers,
        [action.offerId]: action.data,
      },
      offerSummaries: omitKeys([action.offerId], state.offerSummaries),
      offersLoading: {
        ...state.offersLoading,
        [action.offerId]: false,
      },
    }),
    failure: fetchOfferError,
  },
  [FETCH_BEDBANK_OFFER_SUMMARIES]: {
    request: (state, action) => ({
      offerSummariesLoading: {
        ...state.offerSummariesLoading,
        ...arrayToObject(action.offerIds, (id: string) => id, () => true),
      },
      offerSummaryErrors: {
        ...state.offerSummaryErrors,
        ...arrayToObject(action.offerIds, (id: string) => id, () => undefined),
      },
    }),
    success: (state, action) => ({
      bedbankOfferSummaries: {
        ...state.bedbankOfferSummaries,
        ...arrayToObject<App.BedbankOffer>(action.data, (offer) => offer.id),
      },
      offerSummariesLoading: {
        ...state.offerSummariesLoading,
        ...arrayToObject(
          action.offerIds,
          (id: string) => id,
          () => false,
        ),
      },
    }),
    failure: (state, action) => ({
      offerSummaryErrors: {
        ...state.offerSummaryErrors,
        ...arrayToObject(action.offerIds, (id: string) => id, () => action.error),
      },
      offerSummariesLoading: {
        ...state.offerSummariesLoading,
        ...arrayToObject(action.offerIds, (id: string) => id, () => false),
      },
    }),
  },
  [FETCH_BEDBANK_OFFER_RATES]: {
    request: (state, action) => ({
      bedbankOfferRatesLoading: {
        ...state.bedbankOfferRatesLoading,
        [action.offerId]: {
          ...state.bedbankOfferRatesLoading[action.offerId],
          [action.filterKey]: true,
        },
      },
      offerRatesLoading: {
        ...state.offerRatesLoading,
        [action.offerId]: true,
      },
    }),
    success: (state, action) => ({
      bedbankOfferRates: {
        ...state.bedbankOfferRates,
        [action.offerId]: {
          ...state.bedbankOfferRates[action.offerId],
          [action.filterKey]: action.data,
        },
      },
      bedbankOfferRatesLoading: {
        ...state.bedbankOfferRatesLoading,
        [action.offerId]: {
          ...state.bedbankOfferRatesLoading[action.offerId],
          [action.filterKey]: false,
        },
      },
      offerRatesLoading: {
        ...state.offerRatesLoading,
        [action.offerId]: false,
      },
    }),
    failure: (state, action) => ({
      bedbankOfferRatesLoading: {
        ...state.bedbankOfferRatesLoading,
        [action.offerId]: {
          ...state.bedbankOfferRatesLoading[action.offerId],
          [action.filterKey]: false,
        },
      },
      offerRatesLoading: {
        ...state.offerRatesLoading,
        [action.offerId]: false,
      },
    }),
  },
  [FETCH_BEDBANK_OFFER_CALENDAR]: {
    request: (state, action) => ({
      bedbankOfferCalendarLoading: {
        ...state.bedbankOfferCalendarLoading,
        [action.offerId]: true,
      },
    }),
    success: (state, action) => ({
      bedbankOfferCalendar: {
        ...state.bedbankOfferCalendar,
        [action.offerId]: {
          ...state.bedbankOfferCalendar[action.offerId],
          [action.filterKey]: action.data,
        },
      },
      bedbankOfferCalendarLoading: {
        ...state.bedbankOfferCalendarLoading,
        [action.offerId]: false,
      },
    }),
    failure: (state, action) => ({
      bedbankOfferCalendarLoading: {
        ...state.bedbankOfferCalendarLoading,
        [action.offerId]: false,
      },
    }),
  },
  [FETCH_BEDBANK_OFFER_RATES_BULK]: {
    request: (state, action) => ({
      offerRatesLoading: {
        ...state.offerRatesLoading,
        [action.offerId]: true,
      },
      offersRatesBulkLoading: true,
    }),
    success: (state, action) => ({
      bedbankOfferRates: {
        ...state.bedbankOfferRates,
        [action.offerId]: {
          ...state.bedbankOfferRates[action.offerId],
          ...arrayToObject<{ filterKey: string, rates: Array<App.BedbankRate> }, any>(
            action.data,
            (data) => data.filterKey,
            (data) => data.rates,
          ),
        },
      },
      offerRatesLoading: {
        ...state.offerRatesLoading,
        [action.offerId]: false,
      },
      offersRatesBulkLoading: false,
    }),
    failure: (state, action) => ({
      offerRatesLoading: {
        ...state.offerRatesLoading,
        [action.offerId]: false,
      },
      offersRatesBulkLoading: false,
    }),
  },
  [FETCH_BEDBANK_OFFERS_RATES]: {
    request: (state, action) => ({
      bedbankOfferRatesLoading: {
        ...state.bedbankOfferRatesLoading,
        ...arrayToObject(action.offerIds,
          (id: string) => id,
          (id) => ({
            ...state.bedbankOfferRatesLoading[id],
            [action.filterKey]: true,
          }),
        ),
      },
      offerRatesLoading: {
        ...state.offerRatesLoading,
        ...arrayToObject(action.offerIds, (id: string) => id, () => true),
      },
    }),
    success: (state, action) => ({
      bedbankOfferRates: {
        ...state.bedbankOfferRates,
        ...arrayToObject<{ offerId: string, rates: Array<App.BedbankRate> }, any>(
          action.data,
          (data) => data.offerId,
          (data) => ({
            ...state.bedbankOfferRates[data.offerId],
            [action.filterKey]: data.rates,
          }),
        ),
      },
      bedbankOfferRatesLoading: {
        ...state.bedbankOfferRatesLoading,
        ...arrayToObject(action.offerIds,
          (id: string) => id,
          (id) => ({
            ...state.bedbankOfferRatesLoading[id],
            [action.filterKey]: false,
          }),
        ),
      },
      offerRatesLoading: {
        ...state.offerRatesLoading,
        ...arrayToObject(
          action.offerIds,
          (id: string) => id,
          () => false,
        ),
      },
    }),
    failure: (state, action) => ({
      bedbankOfferRatesLoading: {
        ...state.bedbankOfferRatesLoading,
        ...arrayToObject(action.offerIds,
          (id: string) => id,
          (id) => ({
            ...state.bedbankOfferRatesLoading[id],
            [action.filterKey]: false,
          }),
        ),
      },
      offerRatesLoading: {
        ...state.offerRatesLoading,
        ...arrayToObject(action.offerIds, (id: string) => id, () => false),
      },
    }),
  },
  [FETCH_TOURV2_OFFER]: {
    request: fetchOfferRequest,
    success: (state, action) => {
      return {
        tourV2Offers: {
          ...state.tourV2Offers,
          [action.offerId]: action.data,
        },
        offersLoading: {
          ...state.offersLoading,
          [action.offerId]: false,
        },
      }
    },
    failure: fetchOfferError,
  },
  [FETCH_SURCHARGE_MARGIN]: {
    request: (state, action) => {
      return {
        surchargeMargins: {
          ...state.surchargeMargins,
          [action.surchargeMarginKey]: {
            status: 'pending',
          },
        },
      }
    },
    success: (state, action) => {
      return {
        surchargeMargins: {
          ...state.surchargeMargins,
          [action.surchargeMarginKey]: action.data,
        },
      }
    },
    failure: (state, action) => {
      return {
        surchargeMargins: {
          ...state.surchargeMargins,
          [action.surchargeMarginKey]: { status: 'failed' },
        },
      }
    },
  },
  [FETCH_OFFER_FLIGHT_PRICE]: {
    success: (state, action) => {
      const prevOffer = state.offers[action.offerId]
      return ({
        offers: {
          ...state.offers,
          [action.offerId]: {
            ...prevOffer,
            flightPrices: {
              ...('flightPrices' in prevOffer ? prevOffer.flightPrices : {}),
              [action.flightOrigin]: action.data,
            },
          },
        },
      })
    },
  },
  [FETCH_RELATED_TRAVEL_ITEMS]: {
    success: (state, action) => ({
      relatedTravelItems: {
        ...state.relatedTravelItems,
        [action.offerId]: action.data,
      },
    }),
  },
  [FETCH_ALTERNATIVE_OFFERS]: {
    success: (state, action) => ({
      alternativeOffersForProperty: {
        ...state.alternativeOffersForProperty,
        [action.key]: action.data,
      },
    }),
  },
  [FETCH_METASEARCH_OFFER]: {
    request: (state, action) => ({
      offersWithDiscount: {
        ...state.offersWithDiscount,
        [action.key]: { fetching: true },
      },
      // Prevent duplicate tile-level fetches while metasearch discount payload is in-flight.
      offersLoading: {
        ...state.offersLoading,
        [action.offerId]: true,
      },
    }),
    success: (state, action) => {
      const { key, offerId, offer, discountedPackages } = action.data
      return {
        offers: {
          ...state.offers,
          [offerId]: offer as App.Offer,
        },
        offersWithDiscount: {
          ...state.offersWithDiscount,
          [key]: {
            fetching: false,
            discountedPackages,
          },
        },
        offersLoading: {
          ...state.offersLoading,
          [offerId]: false,
        },
      }
    },
    failure: (state, action) => ({
      offersWithDiscount: {
        ...state.offersWithDiscount,
        [action.key]: { fetching: false },
      },
      offersLoading: {
        ...state.offersLoading,
        [action.offerId]: false,
      },
    }),
  },
  [FETCH_PACKAGE_RECOMMENDATIONS]: {
    request: (state, action) => ({
      packageRecommendations: {
        ...state.packageRecommendations,
        [action.key]: { fetching: true },
      },
    }),
    success: (state, action) => ({
      packageRecommendations: {
        ...state.packageRecommendations,
        [action.key]: {
          data: action.data.result,
          fetching: false,
        },
      },
    }),
    failure: (state, action) => ({
      packageRecommendations: {
        ...state.packageRecommendations,
        [action.key]: {
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
})

const offersReducer = createAppReducer<App.OfferState>(initialOfferState, {
  [API_CALL_REQUEST]: apiCalls,
  [API_CALL_FAILURE]: apiCalls,
  [API_CALL_SUCCESS]: apiCalls,
  [OFFERS_CLEAR]: (state, action) => {
    const { offerIds, bedbankOfferIds, tourV2OfferIds } = action.ids
    const newState: App.OfferState = { ...state }
    if (offerIds) { newState.offers = omitKeys(offerIds, state.offers) }
    if (bedbankOfferIds) { newState.bedbankOffers = omitKeys(bedbankOfferIds, state.bedbankOffers) }
    if (tourV2OfferIds) { newState.tourV2Offers = omitKeys(tourV2OfferIds, state.tourV2Offers) }
    if (tourV2OfferIds) {
      newState.tourV2SelectedPackageType = omitKeys(
        tourV2OfferIds,
        state.tourV2SelectedPackageType ?? {},
      )
    }
    return newState
  },
  [SET_TOUR_V2_SELECTED_PACKAGE_TYPE]: (state, action) => {
    const { offerId, packageType } = action
    if (!offerId) return

    return {
      tourV2SelectedPackageType: packageType
        ? { ...(state.tourV2SelectedPackageType ?? {}), [offerId]: packageType }
        : omitKeys([offerId], state.tourV2SelectedPackageType ?? {}),
    }
  },
  [CLEAR_OFFERS_WITH_DISCOUNT]: () => ({
    offersWithDiscount: {},
  }),
  [OFFER_SET_SEARCH_RESULT_METADATA]: (state, action) => {
    const offerListKey = action.offerMetaDataKey
    const existingList = state.searchResultMetadata.offerMetaData[offerListKey]
    const incomingList = action.data.offerMetaData[offerListKey]
    const pendingForList = state.searchResultMetadata.pendingOfferDescriptions?.[offerListKey]
    const mergedList = mergeOfferListMetaDataWithDescriptions(existingList, incomingList, pendingForList)

    const remainingPending = pendingForList ? { ...pendingForList } : undefined
    if (remainingPending && incomingList) {
      for (const offerId of Object.keys(incomingList)) {
        if (mergedList[offerId]?.description) {
          delete remainingPending[offerId]
        }
      }
    }

    return {
      searchResultMetadata: {
        ...state.searchResultMetadata,
        distanceFromSearchTarget: {
          ...state.searchResultMetadata.distanceFromSearchTarget,
          [action.searchTargetId]: { ...state.searchResultMetadata.distanceFromSearchTarget[action.searchTargetId], ...action.data.distanceFromSearchTarget[action.searchTargetId] },
        },
        offerAvailabilityFromSearchTarget: {
          ...state.searchResultMetadata.offerAvailabilityFromSearchTarget,
          [action.availabilityKey]: { ...state.searchResultMetadata.offerAvailabilityFromSearchTarget[action.availabilityKey], ...action.data.offerAvailabilityFromSearchTarget[action.availabilityKey] },
        },
        offerSuggestedDates: {
          ...state.searchResultMetadata.offerSuggestedDates,
          [action.suggestedDatesKey]: { ...state.searchResultMetadata.offerSuggestedDates[action.suggestedDatesKey], ...action.data.offerSuggestedDates[action.suggestedDatesKey] },
        },
        offerMetaData: {
          ...state.searchResultMetadata.offerMetaData,
          [offerListKey]: mergedList,
        },
        pendingOfferDescriptions: {
          ...state.searchResultMetadata.pendingOfferDescriptions,
          [offerListKey]: remainingPending && Object.keys(remainingPending).length > 0 ? remainingPending : undefined,
        },
      },
    }
  },
  [OFFER_SET_TOUR_SEARCH_RESULT_METADATA]: (state, action) => ({
    tourSearchResultMetadata: {
      ...state.tourSearchResultMetadata,
      tourMetadata: {
        ...state.tourSearchResultMetadata.tourMetadata,
        [action.tourMetadataKey]: {
          ...state.tourSearchResultMetadata.tourMetadata[action.tourMetadataKey],
          ...action.data.tourMetadata[action.tourMetadataKey],
        },
      },
    },
  }),
  [INITIALISE_BNBL_MODAL]: (_state, action) => {
    const bnblRoomPackages = action.packages.map(p => {
      const occupancies = p.occupancies ?? new Array(p.numberOfRooms).fill(DEFAULT_OCCUPANCY_FOR_BNBL)

      return {
        numberOfRooms: p.numberOfRooms,
        packageView: p.packageView,
        occupancies,
        isGift: action.isGift,
      }
    })

    return {
      bnblModal: {
        [action.offerId]: bnblRoomPackages,
      },
    }
  },
  [UPDATE_GIFTING_BNBL_MODAL]: (state, action) => {
    const updatedPackages = state.bnblModal[action.offerId].map(item => ({ ...item, isGift: action.isGift }))
    return {
      bnblModal: {
        ...state.bnblModal,
        [action.offerId]: updatedPackages,
      },
    }
  },
  [UPDATE_BNBL_OCCUPANCIES]: (state, action) => {
    const index = state.bnblModal[action.offerId].findIndex(p => p.packageView.package.uniqueKey === action.packageUniqueKey)

    state.bnblModal[action.offerId][index] = {
      ...state.bnblModal[action.offerId][index],
      numberOfRooms: action.occupancies.length,
      occupancies: action.occupancies,
    }

    return {
      bnblModal: {
        [action.offerId]: [...state.bnblModal[action.offerId]],
      },
    }
  },
  [UPDATE_BNBL_PACKAGE_VIEW]: (state, action) => {
    const index = state.bnblModal[action.offerId].findIndex(p => p.packageView.package.uniqueKey === action.packageView.package.uniqueKey)

    state.bnblModal[action.offerId][index] = {
      ...state.bnblModal[action.offerId][index],
      packageView: action.packageView,
    }

    return {
      bnblModal: {
        [action.offerId]: [...state.bnblModal[action.offerId]],
      },
    }
  },
  [OFFER_UPDATE_PRICING_DATA]: (state, action) => {
    const offer = state.offers[action.offerId]
    const itemIdx = offer.packages.findIndex(pkg => pkg.uniqueKey === action.uniqueKey)
    if (itemIdx === -1) { return }

    const newPkg = {
      ...offer.packages[itemIdx],
      ...action.data,
      trackingPrice: action.data.price,
    }

    const newPkgList = [...offer.packages.slice(0, itemIdx), newPkg, ...offer.packages.slice(itemIdx + 1)]

    const newOffer = {
      ...offer,
      packages: newPkgList,
    }

    return {
      ...state,
      offers: {
        ...state.offers,
        [action.offerId]: newOffer,
      },
    }
  },
  [BEDBANK_UPDATE_PRICING_DATA]: (state, action) => {
    const { offerId, roomId, roomRateId, isFlightBundle, bedbankRateKey, pricing } = action.data
    const offerRate = state.bedbankOfferRates[offerId]
    if (!offerRate) { return }

    const bedbankRateList = offerRate[bedbankRateKey]
    if (!bedbankRateList) { return }

    const roomRateIdx = bedbankRateList.findIndex((bbRate: App.BedbankRate) => bbRate.roomId === roomId && bbRate.id === roomRateId && bbRate.isFlightBundle === isFlightBundle)
    if (roomRateIdx === -1) { return }

    const roomRate = bedbankRateList[roomRateIdx]
    if (roomRate.occupancyPricing.length != pricing.data.length) { return }

    const newOccupancyPricing = roomRate.occupancyPricing.map((oldOccupancyPricing, idx) => {
      const newRoomPricing = pricing.data[idx]
      // Assume taxesAndFees are constant
      // update price and/or property fees
      const oldFees = sum(oldOccupancyPricing.fees, ({ amount }) => amount)
      return {
        ...oldOccupancyPricing,
        ...((newRoomPricing.inclusive !== undefined && newRoomPricing.inclusive !== 0) && {
          inclusive: newRoomPricing.inclusive,
          exclusive: newRoomPricing.inclusive - oldOccupancyPricing.taxesAndFees,
          total: newRoomPricing.inclusive + oldFees,
        }),
        ...((newRoomPricing.memberInclusive !== undefined && newRoomPricing.memberInclusive !== oldOccupancyPricing.memberInclusive) && {
          memberInclusive: newRoomPricing.memberInclusive,
          // We expect the new LuxPlus+ price to be positive and greater than the old taxes/fees.
          memberExclusive: newRoomPricing.memberInclusive - oldOccupancyPricing.taxesAndFees,
          memberTotal: newRoomPricing.memberInclusive + oldFees,
        }),
        ...(newRoomPricing.propertyFees !== undefined && { propertyFees: newRoomPricing.propertyFees }),
      }
    })

    const newTotals: App.PackageRoomTotals = newOccupancyPricing.reduce((acc, {
      exclusive,
      inclusive,
      memberExclusive = 0,
      memberInclusive = 0,
      taxesAndFees,
      propertyFees = 0,
    }) => ({
      exclusive: acc.exclusive + exclusive,
      inclusive: acc.inclusive + inclusive,
      total: acc.total + inclusive + propertyFees,
      memberExclusive: acc.memberExclusive + memberExclusive,
      memberInclusive: acc.memberInclusive + memberInclusive,
      memberTotal: acc.memberTotal + memberInclusive + propertyFees,
      taxesAndFees: acc.taxesAndFees + taxesAndFees,
      propertyFees: acc.propertyFees + propertyFees,
    }), {
      exclusive: 0,
      inclusive: 0,
      total: 0,
      memberExclusive: 0,
      memberInclusive: 0,
      memberTotal: 0,
      taxesAndFees: 0,
      propertyFees: 0,
    })

    const newRoomRate = {
      ...roomRate,
      occupancyPricing: newOccupancyPricing,
      totals: newTotals,
    }

    // preserve ordering
    const newBedbankRateList = [...bedbankRateList.slice(0, roomRateIdx), newRoomRate, ...bedbankRateList.slice(roomRateIdx + 1)]
    const newOfferRate = { ...offerRate, [bedbankRateKey]: newBedbankRateList }
    const newBedbankOfferRates = { ...state.bedbankOfferRates, [offerId]: newOfferRate }

    return {
      bedbankOfferRates: newBedbankOfferRates,
    }
  },
  [PROPERTY_UPDATE_PRICING_DATA]: (state, action) => {
    const { key, roomId, rateId, newPrice, newLuxPlusPrice, newPropertyFees } = action

    const rooms = state.propertyRates[key]?.rooms
    if (!rooms) return
    const roomIdx = rooms.findIndex(r => r.roomId === roomId)
    if (roomIdx === -1) return
    const room = rooms[roomIdx]
    if (!('rates' in room)) return
    const rates = room.rates
    if (!(rateId in rates)) return
    const rate = rates[rateId]

    // If bundled experiences are involved, we'll assume the overhead is consistent between
    // non-LuxPlus+ and LuxPlus+ pricing.
    const bundleOverheadPrice = rate.bundle
      ? floatify(rate.totals.sellingPrice - rate.bundle.hotelSellingPrice)
      : 0
    const newRateBundle: Pick<App.RawPropertyRate, 'bundle'> = {
      bundle: rate.bundle && {
        ...rate.bundle,
        ...(newPrice != null && {
          hotelSellingPrice: floatify(newPrice - bundleOverheadPrice),
        }),
        ...(newLuxPlusPrice != null && {
          hotelLuxPlusSellingPrice: floatify(newLuxPlusPrice - bundleOverheadPrice),
        }),
      },
    }
    const newRateTotals: Partial<
      Pick<App.RawPropertyRate, 'totals' | 'luxPlusTotals'>
    > = mergeObjects([
      ['totals' as const, newPrice] as const,
      ['luxPlusTotals' as const, newLuxPlusPrice] as const,
    ].map(([totalsKey, newSellingPrice]) => {
      // We don't need to patch anything.
      if (newSellingPrice == null) return EmptyObject

      const totals = rate[totalsKey]
      // We can't patch what we don't have.
      if (!totals) return EmptyObject

      // We'll assume `taxesAndFees` hasn't changed.
      const newBeforeTaxesFeesPrice = newSellingPrice - totals.taxesAndFees
      const newDisplayPrice = newSellingPrice + newPropertyFees
      // We'll assume `valuedUpToPrice` hasn't changed.
      const newDiscount = getValueOffPercent(totals.valuedUpToPrice, newDisplayPrice)
      const newTotals = {
        ...totals,
        beforeTaxesFeesPrice: newBeforeTaxesFeesPrice,
        sellingPrice: newSellingPrice,
        // TODO(HP-1329): Remove `societeHotelPrice` after confirming it's equivalent to `sellingPrice`.
        societeHotelPrice: newSellingPrice,
        propertyFees: newPropertyFees,
        displayPrice: newDisplayPrice,
        discount: newDiscount,
        // We haven't enough information to update `mx`, `margin`, and `marginAud`.
      }

      return { [totalsKey]: newTotals }
    }))
    const newRate = { ...rate, ...newRateBundle, ...newRateTotals }
    const newRates = { ...room.rates, [rateId]: newRate }
    const newRoom: App.RawAvailablePropertyRoomRates = { ...room, rates: newRates }
    return {
      propertyRates: {
        ...state.propertyRates,
        [key]: {
          ...state.propertyRates[key],
          rooms: [...rooms.slice(0, roomIdx), newRoom, ...rooms.slice(roomIdx + 1)],
        },
      },
    }
  },
  [OFFER_UPDATE_AVAILABLE_RATES]: (state, action) => {
    if (action.key in state.offerAvailableRatesByOccupancy[action.offerId]) {
      return {
        offerAvailableRates: {
          ...state.offerAvailableRates,
          [action.offerId]: {
            ...state.offerAvailableRatesByOccupancy[action.offerId],
            [action.key]: state.offerAvailableRatesByOccupancy[action.offerId][action.key],
          },
        },
      }
    }
    return
  },
  [OFFER_PUSH_TO_LIST_BOTTOM]: (state, action) => {
    const { offerId, listKey, reason } = action
    const offerList = state.offerLists[listKey]
    const adjustedOffers = state.offerListAdjustedOffers[listKey] || {}
    if (!offerList) { return }

    const offerIds = offerList.offerIds.filter(id => id !== offerId)
    offerIds.push(offerId)
    adjustedOffers[offerId] = reason

    return {
      offerLists: {
        ...state.offerLists,
        [listKey]: {
          ...offerList,
          offerIds,
        },
      },
      offerListAdjustedOffers: {
        ...state.offerListAdjustedOffers,
        [listKey]: adjustedOffers,
      },
    }
  },
  [REMOVE_OFFER]: (state, action) => {
    const offerStates = [
      state.offers,
      state.bedbankOffers,
      state.tourV2Offers,
      state.tourV2SelectedPackageType ?? {},
      state.offerSummaries,
      state.bedbankOfferSummaries,
    ]

    const [
      offers,
      bedbankOffers,
      tourV2Offers,
      tourV2SelectedPackageType,
      offerSummaries,
      bedbankOfferSummaries,
    ] = offerStates.map<any>(offerState => {
      if (offerState[action.offerId]) {
        const nextState = { ...offerState }
        delete nextState[action.offerId]
        return nextState
      }
      return offerState
    })

    return {
      offers,
      bedbankOffers,
      tourV2Offers,
      tourV2SelectedPackageType,
      offerSummaries,
      bedbankOfferSummaries,
    }
  },
  [ADD_OFFER_LIST_EXTRA]: (state, action) => {
    const { key, extra } = action
    const offerList = state.offerLists[key]

    if (!offerList) {
      return
    }

    const newExtra: App.OfferListExtra = {
      ...extra,
      position: offerList.offerIds?.length ?? extra.position,
    }

    const existingExtras = offerList.extras || []
    const alreadyExists = existingExtras.some(existing => existing.type === extra.type)

    // Prevent duplicate separators of the same type
    // which can occur from socket reconnections or duplicate server messages
    // If a separator of the same type already exists, ignore the new one
    if (alreadyExists) {
      return
    }

    const nextExtras: Array<App.OfferListExtra> = [...existingExtras, newExtra]

    return {
      offerLists: {
        ...state.offerLists,
        [key]: {
          ...offerList,
          extras: nextExtras,
        },
      },
    }
  },
  [END_OFFER_LIST_STREAM]: (state, action) => {
    const { key, searchId } = action
    const offerList = state.offerLists[key]

    if (offerList?.searchId !== searchId) {
      return
    }

    return {
      offerLists: {
        ...state.offerLists,
        [key]: {
          ...offerList,
          streamEnded: true,
          fetching: false,
        },
      },
      offerListFilterOptions: {
        ...state.offerListFilterOptions,
        [key]: state.offerListFilterOptions[key] ? {
          ...state.offerListFilterOptions[key],
          fetching: false,
        } : undefined,
      },
    }
  },
  [SET_OFFER_LIST_ERROR]: (state, action) => {
    const prevState = state.offerLists[action.key]

    if (!prevState) {
      return
    }

    return {
      offerLists: {
        ...state.offerLists,
        [action.key]: {
          ...prevState,
          error: action.error,
          ...(action.socketListenersStatus !== undefined && { socketListenersStatus: action.socketListenersStatus }),
        },
      },
    }
  },
  [SET_OFFER_LIST_SOCKET_STATUS]: (state, action) => {
    const { key, status } = action
    const offerList = state.offerLists[key]

    if (!offerList) {
      return
    }

    return {
      offerLists: {
        ...state.offerLists,
        [key]: {
          ...offerList,
          socketListenersStatus: status,
        },
      },
    }
  },
  [SET_OFFER_LIST_BOUNDS]: (state, action) => {
    const { key, bounds, centroids } = action
    const offerList = state.offerLists[key]

    if (!offerList) {
      return
    }

    return {
      offerLists: {
        ...state.offerLists,
        [key]: {
          ...offerList,
          bounds,
          centroids,
        },
      },
    }
  },
  [SET_OFFER_LIST_DESCRIPTION_LOADING]: (state, action) => {
    const { key } = action
    const offerList = state.offerLists[key]

    if (!offerList) {
      return
    }

    return {
      offerLists: {
        ...state.offerLists,
        [key]: {
          ...offerList,
          descriptionLoading: true,
        },
      },
    }
  },
  [SET_OFFER_LIST_VALID_OFFER]: (state, action) => {
    const { key, valid } = action
    const offerList = state.offerLists[key]

    if (!offerList) {
      return
    }

    // If the offer is invalid (valid === false), increment the invalid count.
    const currentInvalidCount = offerList.numberOfInvalidOffers ?? 0
    const incrementInvalid = valid ? 0 : 1
    const updatedInvalidCount = currentInvalidCount + incrementInvalid

    // If the offer is valid (valid === true), increment the invalid count.
    const currentValidCount = offerList.numberOfValidOffers ?? 0
    const incrementValid = valid ? 1 : 0
    const updatedValidCount = currentValidCount + incrementValid

    return {
      offerLists: {
        ...state.offerLists,
        [key]: {
          ...offerList,
          numberOfInvalidOffers: updatedInvalidCount,
          numberOfValidOffers: updatedValidCount,
        },
      },
    }
  },
  [UPDATE_OFFER_METADATA_DESCRIPTION]: (state, action) => {
    const { offerListKey, offerId, description } = action
    const list = state.searchResultMetadata?.offerMetaData?.[offerListKey]
    const meta = list?.[offerId]

    if (!list || !meta) {
      return {
        searchResultMetadata: {
          ...state.searchResultMetadata,
          pendingOfferDescriptions: {
            ...state.searchResultMetadata.pendingOfferDescriptions,
            [offerListKey]: {
              ...(state.searchResultMetadata.pendingOfferDescriptions?.[offerListKey] || {}),
              [offerId]: description,
            },
          },
        },
      }
    }

    const pendingForList = state.searchResultMetadata.pendingOfferDescriptions?.[offerListKey]
    const nextPendingForList = pendingForList ? { ...pendingForList } : undefined
    if (nextPendingForList) {
      delete nextPendingForList[offerId]
    }

    return {
      searchResultMetadata: {
        ...state.searchResultMetadata,
        pendingOfferDescriptions: {
          ...state.searchResultMetadata.pendingOfferDescriptions,
          [offerListKey]: nextPendingForList && Object.keys(nextPendingForList).length > 0 ? nextPendingForList : undefined,
        },
        offerMetaData: {
          ...state.searchResultMetadata.offerMetaData,
          [offerListKey]: {
            ...list,
            [offerId]: {
              ...meta,
              description,
            },
          },
        },
      },
    }
  },
  [SET_OFFER_LIST_SELECTED_FILTERS]: (state, action) => {
    const { key, selectedFilters } = action
    const offerList = state.offerLists[key]

    if (!offerList) {
      return
    }

    return {
      offerLists: {
        ...state.offerLists,
        [key]: {
          ...offerList,
          selectedFilters,
        },
      },
    }
  },
})

export default offersReducer
